import React, { useState, useEffect, useCallback, useMemo } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import SubjectCard from "../components/subjects/SubjectCard";
import CreateSubjectModal from "../components/subjects/CreateSubjectModal";
import UpdateSubjectModal from "../components/subjects/UpdateSubjectModal";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getSubjects,
  deleteSubject,
  resetSubjectServerStatus,
} from "../redux/actions/subjectActions";
import { SubjectResponseDto } from "../shared/dtos/subject/SubjectResponseDto";

const SubjectsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- REDUX SELECTORS ---
  const {
    items: subjects,
    loadingFetchAll: loadingSubjects,
    errorFetchAll: errorLoadingSubjects,
    serverStatus,
    loadingCreate: loadingSubjectCreate,
    errorCreate: errorCreatingSubject,
    loadingUpdate: loadingSubjectUpdate,
    errorUpdate: errorUpdatingSubject,
    loadingDelete: loadingSubjectDelete,
  } = useAppSelector((state) => state.subject);

  // --- LOCAL FILTER STATE ---
  const [searchTerm, setSearchTerm] = useState<string>("");

  // --- MODAL STATE ---
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedSubject, setSelectedSubject] =
    useState<SubjectResponseDto | null>(null);

  // --- PERMISSIONS ---
  const canCreate = hasPermission("subject:create");
  const canUpdate = hasPermission("subject:update");
  const canDelete = hasPermission("subject:delete");

  // --- FETCH DATA ---
  const fetchSubjects = useCallback((): void => {
    dispatch(getSubjects());
  }, [dispatch]);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  // --- FRONTEND FILTERING & SORTING ---
  const filteredSubjects = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return subjects
      .filter((subject) => {
        if (!query) return true;

        const matchesName = subject.name?.toLowerCase().includes(query);
        const matchesCode = (subject as any).code
          ?.toLowerCase()
          .includes(query);
        const matchesDepartment = (subject as any).departmentName
          ?.toLowerCase()
          .includes(query);

        return Boolean(matchesName || matchesCode || matchesDepartment);
      })
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [subjects, searchTerm]);

  // --- MODAL HANDLERS ---
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);

  const handleCloseCreate = useCallback((): void => {
    if (!loadingSubjectCreate) setIsCreateModalOpen(false);
  }, [loadingSubjectCreate]);

  const handleOpenUpdate = (subject: SubjectResponseDto): void => {
    setSelectedSubject(subject);
    setIsUpdateModalOpen(true);
  };

  const handleCloseUpdate = useCallback((): void => {
    if (!loadingSubjectUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedSubject(null);
    }
  }, [loadingSubjectUpdate]);

  const handleOpenDelete = (subject: SubjectResponseDto): void => {
    setSelectedSubject(subject);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDelete = useCallback((): void => {
    if (!loadingSubjectDelete) {
      setIsDeleteModalOpen(false);
      setSelectedSubject(null);
    }
  }, [loadingSubjectDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedSubject) {
      dispatch(deleteSubject(selectedSubject.id));
    }
  };

  // --- SERVER STATUS HANDLING ---
  useEffect(() => {
    if (!serverStatus) return;

    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) {
      handleCloseCreate();
    }
    if (operation === "update" && status === 200) {
      handleCloseUpdate();
    }
    if (operation === "delete" && status === 200) {
      handleCloseDelete();
    }

    dispatch(resetSubjectServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Subjects"
      subtitle="Manage academic subjects and their grade level allocations."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Subject
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search subjects by name or code..."
      />

      <CardGridContainer
        loading={loadingSubjects}
        error={errorLoadingSubjects}
        itemCount={filteredSubjects.length}
        emptyMessage="No subjects match your search criteria"
        onRetry={fetchSubjects}
      >
        {filteredSubjects.map((subject) => (
          <SubjectCard
            key={subject.id}
            subject={subject}
            onEdit={canUpdate ? () => handleOpenUpdate(subject) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(subject) : undefined}
          />
        ))}
      </CardGridContainer>

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateSubjectModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingSubjectCreate}
          error={errorCreatingSubject}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedSubject && (
        <UpdateSubjectModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          subject={selectedSubject}
          loading={loadingSubjectUpdate}
          error={errorUpdatingSubject}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedSubject && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Subject"
          description={`Are you sure you want to delete "${selectedSubject.name}"? This action cannot be undone.`}
          loading={loadingSubjectDelete}
        />
      )}
    </PageContainer>
  );
};

export default SubjectsPage;
